// Copyright 2018 Sisense Inc. All rights reserved.

/**
 * @fileoverview Abstract class for all UI components. This defines the standard
 * design pattern that all UI components should follow.
 * @author artem.lit@sisense.com (Artem Lytvynov).
 * @copyright Sisense Inc.
 * @licence Sisense License.
 */

'use strict';

const EventTarget = require('@sbi/core.events').EventTarget;
const events = require('./events');

/**
 * Default implementation of UI component.
 * @extends {EventTarget}
 */
class BasicComponent extends EventTarget {
    /**
     * Class constructor.
     * @constructor
     */
    constructor() {
        super();

        /**
         * @type {Document}
         * @private
         */
        this.document_ = window.document;

        /**
         * The DOM element for the component.
         * @type {Element}
         * @private
         */
        this.element_ = this.document_.createElement('div');
        this.element_.id = String(this.getUid());

        /**
         * Whether the component is in the document.
         * @type {boolean}
         * @private
         */
        this.rendered_ = false;

        /**
         * Arbitrary data object associated with the component.
         * @type {*}
         * @private
         */
        this.model_ = null;
    }

    /**
     * Renders the component.  If a parent element is supplied, the
     * component's element will be appended to it.  If there is no optional
     * parent element and the element doesn't have a parentNode then it will
     * be appended to the document body.
     * Throws an Error if the component is already rendered.
     * @param {Element=} opt_parent Optional parent element.
     * @param {Node=} opt_before Node before which the component is to be
     * rendered.  If left out the node is appended to the parent element.
     * @throws {Error}
     * @private
     */
    render_(opt_parent, opt_before) {
        if (this.rendered_) {
            throw Error('Component already rendered');
        }
        if (opt_parent) {
            opt_parent.insertBefore(this.element_, opt_before || null);
        } else {
            this.document_.body.appendChild(this.element_);
        }
        this.rendered_ = true;
    }

    /**
     * @override
     * @protected
     */
    disposeInternal() {
        this.dispatchEvent(
            /** @type {Event} */ (new events.ComponentDestroy(this))
        );
        super.disposeInternal();
        if (this.rendered_) {
            this.element_.parentElement.removeChild(this.element_);
            this.rendered_ = false;
        }
        this.document_ = null;
        this.element_ = null;
        this.model_ = null;
    }

    /**
     * Sets the model associated with the UI component.
     * @param {*} obj The model.
     */
    setModel(obj) {
        this.model_ = obj;
    }

    /**
     * Returns the model associated with the UI component.
     * @return {*}
     */
    getModel() {
        return this.model_;
    }

    /**
     * Gets the component's element.
     * @return {Element}
     */
    getElement() {
        return this.element_;
    }

    /**
     * Determines whether the component has been added to the document.
     * @return {boolean}
     */
    isRendered() {
        return this.rendered_;
    }

    /**
     * Renders the component.  If a parent element is supplied, the
     * component's element will be appended to it.  If there is no optional
     * parent element and the element doesn't have a parentNode then it will
     * be appended to the document body.
     * Throws an Error if the component is already rendered.
     * @param {Element=} opt_parent Optional parent element to render.
     */
    render(opt_parent) {
        this.render_(opt_parent);
    }

    /**
     * Renders the component before another element. The other element
     * should be in the document already.
     * Throws an Error if the component is already rendered.
     * @param {Node} sibling Node to render the component before.
     */
    renderBefore(sibling) {
        this.render_(
            /** @type {Element} */ (sibling.parentNode), sibling
        );
    }

    /**
     * Dispatch ComponentReady event. This method should be used by
     * component's Renderer subclass after it will initialize it's internal
     * state, render and append to the document DOM-tree etc. This event
     * tells to the application that current component ready and can be
     * used.
     */
    dispatchReadyEvent() {
        this.dispatchEvent(
            /** @type {Event} */ (new events.ComponentReady(this))
        );
    }
}

// Exports:
exports.BasicComponent = BasicComponent;
